import { LoggingFacade } from '@vwdfive/myvw-logging';
import { IAuthServiceV2 } from '@volkswagen-onehub/authservice';

export interface IVcfCapabilityResponse {
    readonly data?: IVcfCapability[];
}

export interface IVcfCapability {
    readonly id: string;
    readonly status: number[];
    readonly expirationDate?: string | null;
    readonly userDisablingAllowed?: boolean;
}

export interface IVcfRequestIdResponse {
    readonly data?: {
        readonly requestID: string;
    };
}

export interface IVcfUpdateStatusResponse {
    readonly data?: {
        readonly status: string;
    };
}

export interface IVcfCapabilityPostRequestV1
    extends Array<{
        readonly id: string;
        readonly enabled: boolean;
    }> {}

export interface IVcfCapabilityPostRequestV2 {
    readonly capabilities: [
        {
            readonly id: string;
            readonly enabled: boolean;
        },
    ];
}

enum ApplicationVersion {
    V1 = 1,
    V2 = 2,
    V3 = 3,
}

// https://cardata.apps.emea.vwapps.io/documentation#tag/Capabilities
export class VcfCapabilityClient {
    private readonly headers: HeadersInit = {
        'User-Id': '__userId__',
    };

    private readonly resourceHostId: string;

    private getGdcType = (isWcar: boolean) => (isWcar ? `myvw-wcar-${this.stage}` : `myvw-mbb-${this.stage}`);

    constructor(
        private readonly authServiceWeConnect: IAuthServiceV2,
        private readonly loggingFacade: LoggingFacade,
        private readonly stage: string,
    ) {
        this.resourceHostId = `myvw-vcf-${stage}`;
    }

    // https://cardata.apps.emea.vwapps.io/documentation#tag/Capabilities/operation/getUserCapabilities
    public async getUserVehicleCapabilities(vin: string, isWcar: boolean): Promise<IVcfCapabilityResponse> {
        const headers = {
            ...this.headers,
            Accept: `application/json;version=${ApplicationVersion.V3}`,
            data: JSON.stringify({}),
            'Content-Type': `application/json;version=${ApplicationVersion.V3}`,
        };
        const url = `vehicles/${vin}/usercapabilities?gdc=${this.getGdcType(isWcar)}`;
        const response = await this.authServiceWeConnect.get(this.resourceHostId, url, headers);

        if (response.status === 404) {
            return {};
        }

        if (!response.ok) {
            throw new Error(`Request failed with status code ${response.status}`);
        }

        return await response.json();
    }

    // https://cardata.apps.emea.vwapps.io/documentation#tag/Capabilities/operation/postUserCapabilities
    public async setUserVehicleCapabilityEnabled(
        vin: string,
        isWcar: boolean,
        capabilityId: string,
        enabled: boolean,
    ): Promise<IVcfRequestIdResponse | undefined> {
        const headers = {
            ...this.headers,
            Accept: `application/json;version=${ApplicationVersion.V2}`,
            'Content-Type': `application/json;version=${ApplicationVersion.V2}`,
        };
        const url = `vehicles/${vin}/usercapabilities?gdc=${this.getGdcType(isWcar)}`;
        const data: IVcfCapabilityPostRequestV2 = {
            capabilities: [
                {
                    id: capabilityId,
                    enabled: enabled,
                },
            ],
        };
        const response = await this.authServiceWeConnect.post(this.resourceHostId, url, JSON.stringify(data), headers);

        if (!response.ok) {
            this.loggingFacade.error(
                'Could not set user vehicle capability enabled',
                new Error(`Request failed with status code ${response.status}`),
            );
            return undefined;
        }

        return await response.json();
    }

    // https://cardata.apps.emea.vwapps.io/documentation#tag/Capabilities/operation/getSetUsercapabilitiesRequestStatus
    public async getCapabilityUpdateStatus(
        vin: string,
        isWcar: boolean,
        requestId: string,
    ): Promise<IVcfUpdateStatusResponse | undefined> {
        const url = `vehicles/${vin}/usercapabilities/requests/${requestId}?gdc=${this.getGdcType(isWcar)}`;
        const response = await this.authServiceWeConnect.get(this.resourceHostId, url, this.headers);

        if (!response || !response.ok) {
            this.loggingFacade.error('Could not get capability update status', new Error());
            return undefined;
        }

        return await response.json();
    }
}
